import { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Grid2 as Grid,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Stack,
  useTheme,
} from "@mui/material";
import { getThumbnailImage } from "@/utils/media";
import {
  useRedeemPointsMutation,
} from "@/api/loyaltyApi";
import { Content } from "@/types";
import { LoyaltyAccount } from "@/types/loyalty";
import { useAppSelector } from "@/hooks";
import { CheckmarkCircle02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import RewardPointsChip from "./RewardPointsChip";
import LoadingButton from "@mui/lab/LoadingButton";
import LoyaltyRedemptionPrintTemplate from "./LoyaltyRedemptionPrintTemplate";
import { PrinterIcon } from "@hugeicons/core-free-icons";

interface Props {
  open: boolean;
  onClose: () => void;
  reward: Content | null;
  loyaltyAccount: LoyaltyAccount | null;
  client: {
    name?: string;
    lastname?: string;
    phone?: string;
  } | null;
}

const INSTRUCTIONS = [
  {
    label: "Confirmar identidad",
    description: "Solicite la cédula ó licencia de conducir al cliente",
  },
  {
    label: "Verifique los detalles",
    description:
      "Explique al cliente el nombre del producto a canjear asi como los puntos que se costará",
  },
  {
    label: "Imprimir formulario",
    description: "Imprima el formulario de confirmación para que el cliente lo firme",
    action: 'print'
  },
  {
    label: "Confirmar canje",
    description: "Si el cliente acepta presione el botón de canjear",
  },
];

type DialogStatus = "idle" | "success";

const RedemptionDialog = ({
  open,
  onClose,
  reward,
  loyaltyAccount,
  client,
}: Props) => {
  const { palette } = useTheme();
  const printRef = useRef<HTMLDivElement>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [status, setStatus] = useState<DialogStatus>("idle");

  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const adminUid = useAppSelector((store) => store.auth.user?.uid);
  const branchId = useAppSelector((store) => store.ui.activeBranch) || "unknown";

  const [redeemPoints, redeemPointsMutation] = useRedeemPointsMutation();

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "Confirmacion_Canje",
  });

  // Reiniciar estado al abrir el diálogo
  useEffect(() => {
    if (open) {
      setStatus("idle");
      setErrorMessage("");
    }
  }, [open]);

  const handleRedeem = async () => {
    if (!reward || !loyaltyAccount || !adminUid) return;
    setErrorMessage("");

    try {
      await redeemPoints({
        organizationId,
        uid: loyaltyAccount.uid,
        contentId: reward.id,
        rewardName: reward.title,
        pointsCost: reward.loyalty?.pointsCost || 0,
        createdBy: adminUid,
        branchId,
      }).unwrap();
      setStatus("success");
    } catch (err: any) {
      console.error(err);
      setErrorMessage(err?.data?.message || err?.message || "Error al procesar el canje");
    }
  };

  const handleCancel = async () => {
    onClose();
  };

  // Prevenir cierre haciendo clic fuera (backdrop)
  const handleDialogClose = (_event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason && reason === "backdropClick") return;
    if (reason && reason === "escapeKeyDown") return;
    handleCancel();
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth={status === "success" ? "sm" : "md"} fullWidth disableEscapeKeyDown>
      {status !== "success" && (
        <DialogTitle>
          <Typography variant="h3">
            Confirmar Canje
          </Typography>
        </DialogTitle>
      )}

      <DialogContent dividers>
        <Grid container spacing={6}>
          <Grid size={{ xs: 12, md: status === "success" ? 12 : 6 }}>
            {status === "idle" && reward && (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={2} px={3} textAlign="center" height="100%">
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: 2,
                    backgroundImage: `url(${getThumbnailImage({ file: reward.images, organizationId, size: "800x500" })})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    boxShadow: 1,
                  }}
                />
                <Typography variant="h3">
                  {reward.title}
                </Typography>
                <Typography>
                  Al confirmar se descontarán {" "}
                  <RewardPointsChip points={reward.loyalty?.pointsCost || 0} />
                  {" "} del saldo del cliente.
                </Typography>
              </Box>
            )}

            {status === "success" && (
              <Stack flex={1} justifyContent="center" alignItems="center" gap={2} py={3} height="100%">
                <HugeiconsIcon icon={CheckmarkCircle02Icon} size={80} color={palette.success.main} />
                <Typography variant="h1" component="h3" align="center" color="primary.main">
                  El premio ha sido canjeado correctamente.
                </Typography>
                <Typography variant="body2" align="center">
                  Los puntos han sido descontados de la cuenta.
                </Typography>
              </Stack>
            )}
          </Grid>

          {status !== "success" && (
            <Grid size={{ xs: 12, md: 6 }}>
              {errorMessage && (
                <Box pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </Box>
              )}
              <Typography variant="body2" mb={3} color="text.secondary">
                * Instrucciones para ti
              </Typography>
              <Stepper
                orientation="vertical"
              >
                {INSTRUCTIONS.map((step) => (
                  <Step key={step.label} active={true}>
                    <StepLabel>
                      {step.label}
                    </StepLabel>
                    <StepContent>
                      <Typography variant="body1" color="text.secondary">
                        {step.description}
                      </Typography>
                      {step.action === "print" && (
                        <Box mt={2}>
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handlePrint()}
                            startIcon={<HugeiconsIcon icon={PrinterIcon} size={20} />}
                          >
                            Imprimir Formulario
                          </Button>
                        </Box>
                      )}
                    </StepContent>
                  </Step>
                ))}
              </Stepper>
            </Grid>
          )}
        </Grid>

        <Box className="print-only-container">
          <div ref={printRef}>
            {reward && loyaltyAccount && client && (
              <LoyaltyRedemptionPrintTemplate
                reward={reward}
                loyaltyAccount={loyaltyAccount}
                client={client}
              />
            )}
          </div>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit" fullWidth>
          {status === "success" ? "Cerrar" : "Cancelar"}
        </Button>
        {status !== "success" && (
          <LoadingButton
            loading={redeemPointsMutation.isLoading}
            onClick={handleRedeem}
            variant="contained"
            color="success"
            disabled={status !== "idle"}
            fullWidth
          >
            Canjear
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog >
  );
};

export default RedemptionDialog;
